/**
 * H5 当前所在环境判断
 * 相关判断逻辑拷贝自phf项目，未用到的判断先注释掉了，后续如果有需要开启相关判断，需要验证
 *
 *
 * 函数式 API 说明:
 * - 为了支持 SSR 多用户场景，所有环境检测改为函数调用
 * - 客户端调用: getUA(), getPlatform() 等 - 自动从 window 获取 UA 并缓存结果
 * - 服务端调用: getUA(uaString), getPlatform(uaString) 等 - 基于传入的 UA 解析，不使用缓存
 */

import {
  MT_APP_ID,
  HS_APP_ID,
  SYSTEM_PLATFORM,
  RUNTIME_ENV_TYPE,
  CONTAINER_PLATFORM_TYPE,
  C_TYPE_ENUM,
  FP_PLATFORM_ENUM,
  LX_ENV_TYPE,
  WM_APP_ID,
} from "./constant";
import { JumpTypeEnum } from "@/types/jumpPage";
import { getUrlParam } from "./urlParams";

// ============================================================
// 类型定义 (T007, T008)
// ============================================================

/**
 * 环境检测函数类型
 * @template T - 返回值类型
 * @param ua - 可选的 User-Agent 字符串。如果不提供，客户端环境会自动从 window 获取
 * @returns 环境检测结果
 */
export type BrowserEnvGetter<T> = (ua?: string) => T;

/**
 * 解析后的浏览器环境信息
 */
export interface ParsedBrowserEnv {
  /** User-Agent 字符串 */
  ua: string;

  /** 操作系统检测 */
  isIos: boolean;
  isAndroid: boolean;
  isHarmonyOS: boolean;

  /** 容器检测 */
  isMT: boolean;              // 美团 APP
  isWXApp: boolean;           // 微信小程序
  isMTWXApp: boolean;         // 美团小程序
  isWX: boolean;              // 微信浏览器
  isSafari: boolean;
  isBrowser: boolean;         // 普通浏览器
  isHSWxmini: boolean;     // 惠省小程序

  /** KNB 容器检测 */
  isStandardKNBContainer: boolean;
  isTitansContainer: boolean;
  isGrowthContainer: boolean;
  isWXContainer: boolean;

  /** 枚举类型环境标识 */
  platform: SYSTEM_PLATFORM;
  runtimeEnv: RUNTIME_ENV_TYPE;
  containerPlatform: CONTAINER_PLATFORM_TYPE;
  cType: C_TYPE_ENUM;
  fpPlatform: FP_PLATFORM_ENUM;
  lxEnv: LX_ENV_TYPE;

  /** APP 版本号 */
  mtAppVersion: string | undefined;
}


let cachedResult: ParsedBrowserEnv | null = null;

// ============================================================
// 辅助函数
// ============================================================

/**
 * 获取默认 UA 字符串 (T010)
 * - 客户端环境: 从 window.navigator.userAgent 获取
 * - 服务端环境: 返回空字符串
 * @returns UA 字符串（小写）
 */
function getDefaultUA(): string {
  return typeof window !== "undefined"
    ? String(window.navigator.userAgent).toLowerCase()
    : "";
}

// ============================================================
// 原子解析函数 - 单一职责，其他函数复用这些
// ============================================================

/**
 * 判断是否 iOS
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsIos(ua: string): boolean {
  return !!ua.match(/\(i[^;]+;( u;)? cpu.+mac os x/);
}

/**
 * 判断是否 Android
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsAndroid(ua: string): boolean {
  return ua.indexOf("android") > -1 || ua.indexOf("adr") > -1;
}

/**
 * 判断是否 HarmonyOS
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsHarmonyOS(ua: string): boolean {
  return ua.indexOf("harmonyos") > -1 || ua.indexOf("harmony") > -1;
}

/**
 * 判断是否美团 APP
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsMT(ua: string): boolean {
  return !!ua.match(/meituangroup/i);
}

/**
 * 判断是否美团小程序（通过 AppID）
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsMTWXApp(ua: string): boolean {
  return ua.indexOf("miniprogram") > -1 && ua.indexOf(MT_APP_ID) > -1;
}

/**
 * 判断是否微信小程序（任意小程序）
 * 服务端：仅用 UA 判断；客户端：会结合 window.__wxjs_environment，与原有 CSR 语义一致
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsWXAppByUA(ua: string): boolean {
  return !!ua.match(/miniProgram/i) || (typeof window !== "undefined" && window.__wxjs_environment === "miniprogram");
}

/**
 * 判断是否微信小程序（包含客户端环境变量检查）
 * 注意：包含 window.__wxjs_environment 检查，仅客户端使用
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsWXApp(ua: string): boolean {
  return parseIsWXAppByUA(ua) ||
    (typeof window !== "undefined" && window.__wxjs_environment === "miniprogram");
}

/**
 * 判断是否微信浏览器（非小程序、非企业微信）
 * @param ua - User-Agent 字符串（小写）
 * @param isWXApp - 是否微信小程序（需要先解析）
 */
function parseIsWX(ua: string, isWXApp: boolean): boolean {
  return !isWXApp && !ua.match(/wxwork/i) && !!ua.match(/micromessenger/i);
}

/**
 * 判断是否 Safari 浏览器
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsSafari(ua: string): boolean {
  return /Safari/i.test(ua) && !/Chrome/i.test(ua);
}

/**
 * 判断是否 惠省小程序
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsHSWxmini(ua: string): boolean {
  return ua.indexOf(HS_APP_ID) > -1;
}

/**
 * 判断是否 外卖小程序
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsWaimaiWxMini(ua: string): boolean {
  return ua.indexOf(WM_APP_ID) > -1;
}

/**
 * 判断是否标准 KNB 容器（KNB + MSI）
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsStandardKNBContainer(ua: string): boolean {
  return /KNB\//i.test(ua) && /MSI\//i.test(ua) && !parseIsGrowthContainer(ua);
}

/**
 * 判断是否 Titans 容器（KNB 但非标准容器）
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsTitansContainer(ua: string): boolean {
  return /KNB\//i.test(ua) && !parseIsStandardKNBContainer(ua) && !parseIsGrowthContainer(ua);
}

/**
 * 判断是否外投容器
 * @param ua - User-Agent 字符串（小写）
 */
function parseIsGrowthContainer(ua: string): boolean {
  return /growthweb/i.test(ua);
}

/**
 * 判断是否点评 App
 * @param ua - User-Agent 字符串
 */
function parseIsDianping(ua: string): boolean {
  return /dianping/i.test(ua);
}

/**
 * 判断是否外卖 App
 * @param ua - User-Agent 字符串
 */
function parseIsWaimai(ua: string): boolean {
  return /meituanwaimai|meituantakeout|itakeaway/i.test(ua);
}

/**
 * 判断是否非美团的其他微信小程序
 * @param ua - User-Agent 字符串
 */
function parseIsOtherWXMini(ua: string): boolean {
  const isWXAppByUA = parseIsWXAppByUA(ua);
  const isMTWXApp = parseIsMTWXApp(ua);
  return isWXAppByUA && !isMTWXApp;
}

// ============================================================
// UA 解析函数 (T011-T016)
// ============================================================

/**
 * 从 UA 解析系统平台 (T011)
 * @param ua - User-Agent 字符串
 * @returns 系统平台枚举
 */
function parseSystemPlatform(ua: string): SYSTEM_PLATFORM {
  const isHarmonyOS = parseIsHarmonyOS(ua);
  const isIos = parseIsIos(ua);
  const isAndroid = parseIsAndroid(ua);

  if (isHarmonyOS) {
    return SYSTEM_PLATFORM.HARMONY_OS;
  }
  if (isIos) {
    return SYSTEM_PLATFORM.IOS;
  }
  if (isAndroid) {
    return SYSTEM_PLATFORM.ANDROID;
  }
  return SYSTEM_PLATFORM.UNKNOWN;
}

/**
 * 从 UA 解析运行环境 (T012)
 *
 * 判断优先级：
 * 1. 美团小程序（miniprogram + 美团 AppID）→ MT_MINI
 * 2. 美团 App（meituangroup）→ MT_APP
 * 3. 微信浏览器（micromessenger，非小程序、非企业微信）→ WX_BROWSER
 * 4. 点评 APP、外卖 APP、其它微信小程序 -> UNKNOWN 
 * 5. 普通浏览器 → BROWSER
 *
 * @param ua - User-Agent 字符串
 * @returns 运行环境枚举
 */
function parseRuntimeEnv(ua: string): RUNTIME_ENV_TYPE {
  const isMTWXApp = parseIsMTWXApp(ua);
  const isMT = parseIsMT(ua);
  const isWXAppByUA = parseIsWXAppByUA(ua);
  const isWX = parseIsWX(ua, isWXAppByUA);

  // 美团小程序
  if (isMTWXApp) {
    return RUNTIME_ENV_TYPE.MT_MINI;
  }
  // 美团App
  if (isMT) {
    return RUNTIME_ENV_TYPE.MT_APP;
  }
  // 微信浏览器
  if (isWX) {
    return RUNTIME_ENV_TYPE.WX_BROWSER;
  }
  // 点评 APP、外卖 APP、其他小程序（非美团小程序，如点评小程序、惠省小程序等）→ UNKNOWN
  if (parseIsWaimai(ua) || parseIsOtherWXMini(ua) || parseIsDianping(ua)) {
    return RUNTIME_ENV_TYPE.UNKNOWN;
  }
  // 浏览器环境
  return RUNTIME_ENV_TYPE.BROWSER;
}

/**
 * 从 UA 解析灵犀埋点上报的环境
 *
 * @param ua - User-Agent 字符串
 * @returns 运行环境枚举
 */
function parseLxEnv(ua: string): LX_ENV_TYPE {
  const isMTWXApp = parseIsMTWXApp(ua);
  const isMT = parseIsMT(ua);
  const isWXAppByUA = parseIsWXAppByUA(ua);
  const isWX = parseIsWX(ua, isWXAppByUA);

  // 美团小程序
  if (isMTWXApp) {
    return LX_ENV_TYPE.MT_MINI;
  }
  // 美团App
  if (isMT) {
    return LX_ENV_TYPE.MT_APP;
  }
  // 微信浏览器
  if (isWX) {
    return LX_ENV_TYPE.WX_BROWSER;
  }
  // 外卖App
  if (parseIsWaimai(ua)) {
    return LX_ENV_TYPE.WAIMAI_APP;
  }
  // 外卖小程序
  if (parseIsWaimaiWxMini(ua)) {
    return LX_ENV_TYPE.WAIMAI_MINI;
  }
  // 惠省小程序
  if (parseIsHSWxmini(ua)) {
    return LX_ENV_TYPE.HUISHENG_MINI;
  }
  // 点评 APP、其他小程序（非美团小程序，如点评小程序等）→ UNKNOWN
  if (parseIsOtherWXMini(ua) || parseIsDianping(ua)) {
    return LX_ENV_TYPE.UNKNOWN;
  }
  // 浏览器环境
  return LX_ENV_TYPE.BROWSER;
}

/**
 * 从 UA 解析容器平台 (T013)
 * @param ua - User-Agent 字符串
 * @returns 容器平台枚举
 */
function parseContainerPlatform(ua: string): CONTAINER_PLATFORM_TYPE {
  const isMTWXApp = parseIsMTWXApp(ua);
  const isMT = parseIsMT(ua);

  if (isMTWXApp) {
    return CONTAINER_PLATFORM_TYPE.MT_MINI;
  }
  if (isMT) {
    return CONTAINER_PLATFORM_TYPE.MT_APP;
  }
  return CONTAINER_PLATFORM_TYPE.BROWSER;
}

/**
 * 从 UA 解析客户端类型 (T014)
 * @param ua - User-Agent 字符串
 * @returns 客户端类型枚举
 */
export function parseCType(ua: string): C_TYPE_ENUM {
  const isMT = parseIsMT(ua);
  const isMTWXApp = parseIsMTWXApp(ua);
  const isWXAppByUA = parseIsWXAppByUA(ua);
  const isWX = parseIsWX(ua, isWXAppByUA);
  const isIos = parseIsIos(ua);
  const isHarmonyOS = parseIsHarmonyOS(ua);

  if (isMT) {
    if (isIos) return C_TYPE_ENUM.MT_APP_IOS;
    else if (isHarmonyOS) return C_TYPE_ENUM.MT_APP_HARMONY_OS;
    else return C_TYPE_ENUM.MT_APP_ANDROID;
  } else if (isMTWXApp) {
    return C_TYPE_ENUM.MT_MINI;
  } else if (isWX) {
    return C_TYPE_ENUM.WX_H5;
  } else {
    return C_TYPE_ENUM.H5;
  }
}

/**
 * 从 UA 解析风控平台标识 (T015)
 * @param ua - User-Agent 字符串
 * @returns 风控平台枚举
 */
function parseFPPlatform(ua: string): FP_PLATFORM_ENUM {
  const isWXAppByUA = parseIsWXAppByUA(ua);
  const isWX = parseIsWX(ua, isWXAppByUA);
  const isMT = parseIsMT(ua);
  const isIos = parseIsIos(ua);
  const isHarmonyOS = parseIsHarmonyOS(ua);

  if (isWX || isWXAppByUA) return FP_PLATFORM_ENUM.WX;
  if (isMT) {
    if (isIos) return FP_PLATFORM_ENUM.IOS_APP;
    else if (isHarmonyOS) return FP_PLATFORM_ENUM.HM_APP;
    else return FP_PLATFORM_ENUM.ANDROID_APP;
  }
  return FP_PLATFORM_ENUM.BROWSER;
}

/**
 * 从 UA 解析美团 APP 版本号 (T016)
 * @param ua - User-Agent 字符串
 * @returns 版本号字符串（如 "12.3.400"），非美团 APP 返回 undefined
 */
function parseMtAppVersion(ua: string): string | undefined {
  const isMT = parseIsMT(ua);
  if (isMT) {
    const versionPattern = /MeituanGroup\/(\d+\.\d+\.\d+)/i;
    const match = ua.match(versionPattern);
    if (match) {
      return match[1];
    }
  }
  return undefined;
}

// ============================================================
// 供外部模块复用的解析函数 (T034, T035)
// ============================================================

/**
 * 从 UA 解析平台标识（数字）(T034)
 * 供 ssrCommonParams.ts 等外部模块复用
 * @param ua - User-Agent 字符串
 * @returns 平台标识数字 (3=MT_MINI, 6=MT_APP, 2=BROWSER)
 */
export function parsePlatformFromUA(ua: string): number {
  const isMTWXApp = parseIsMTWXApp(ua);
  const isMT = parseIsMT(ua);

  if (isMTWXApp) {
    return 3; // MT_MINI
  }
  if (isMT) {
    return 6; // MT_APP
  }
  return 2; // BROWSER (默认)
}

// ============================================================
// 综合解析函数 (T017)
// ============================================================

/**
 * 解析完整的浏览器环境信息 (T017)
 * @param ua - User-Agent 字符串
 * @returns 解析后的环境信息对象
 */
function parseAppEnv(ua: string): ParsedBrowserEnv {
  const isIos = parseIsIos(ua);
  const isAndroid = parseIsAndroid(ua);
  const isHarmonyOS = parseIsHarmonyOS(ua);
  const isMT = parseIsMT(ua);
  const isWXApp = parseIsWXApp(ua);
  const isMTWXApp = parseIsMTWXApp(ua);
  const isWX = parseIsWX(ua, isWXApp);
  const isBrowser = !(isMT || isMTWXApp || isWX);
  const isSafari = parseIsSafari(ua);
  const isHSWxmini = parseIsHSWxmini(ua);
  const isStandardKNBContainer = parseIsStandardKNBContainer(ua);
  const isTitansContainer = parseIsTitansContainer(ua);
  const isGrowthContainer = parseIsGrowthContainer(ua);
  const isWXContainer = isWXApp;

  return {
    ua,
    isIos,
    isAndroid,
    isHarmonyOS,
    isMT,
    isWXApp,
    isMTWXApp,
    isWX,
    isSafari,
    isBrowser,
    isHSWxmini,
    isStandardKNBContainer,
    isTitansContainer,
    isGrowthContainer,
    isWXContainer,
    platform: parseSystemPlatform(ua),
    runtimeEnv: parseRuntimeEnv(ua),
    lxEnv: parseLxEnv(ua),
    containerPlatform: parseContainerPlatform(ua),
    cType: parseCType(ua),
    fpPlatform: parseFPPlatform(ua),
    mtAppVersion: parseMtAppVersion(ua),
  };
}

// ============================================================
// Getter 函数 (T018-T025)
// ============================================================

/**
 * 获取 UA 字符串 (T018)
 * @param ua - 可选的 UA 字符串
 * @returns UA 字符串
 */
export function getUA(ua?: string): string {
  return (ua || getDefaultUA()).toLowerCase();
}

/**
 * 获取系统平台 (T019)
 * @param ua - 可选的 UA 字符串
 * @returns 系统平台枚举
 */
export function getPlatform(ua?: string): SYSTEM_PLATFORM {
  const uaString = getUA(ua);
  return parseSystemPlatform(uaString);
}

/**
 * 获取运行环境 (T020)
 * @param ua - 可选的 UA 字符串
 * @returns 运行环境枚举
 */
export function getRuntimeEnv(ua?: string): RUNTIME_ENV_TYPE {
  const uaString = getUA(ua);
  return parseRuntimeEnv(uaString);
}

/**
 * 获取容器平台 (T021)
 * @param ua - 可选的 UA 字符串
 * @returns 容器平台枚举
 */
export function getContainerPlatform(ua?: string): CONTAINER_PLATFORM_TYPE {
  const uaString = getUA(ua);
  return parseContainerPlatform(uaString);
}

/**
 * 获取客户端类型 (T022)
 * @param ua - 可选的 UA 字符串
 * @returns 客户端类型枚举
 */
export function getCType(ua?: string): C_TYPE_ENUM {
  const uaString = getUA(ua);
  return parseCType(uaString);
}

/**
 * 获取风控平台标识 (T023)
 * @param ua - 可选的 UA 字符串
 * @returns 风控平台枚举
 */
export function getFPPlatform(ua?: string): FP_PLATFORM_ENUM {
  const uaString = getUA(ua);
  return parseFPPlatform(uaString);
}

/**
 * 获取美团 APP 版本号 (T024)
 * @param ua - 可选的 UA 字符串
 * @returns 版本号字符串，非美团 APP 返回 undefined
 */
export function getMtAppVersion(ua?: string): string | undefined {
  const uaString = getUA(ua);
  return parseMtAppVersion(uaString);
}

/**
 * 获取完整环境信息 (T025)
 * @param ua - 可选的 UA 字符串
 * @returns 解析后的环境信息对象
 */
export function getAppEnv(ua?: string): ParsedBrowserEnv {
  // 客户端缓存逻辑：无参数且已有缓存时返回缓存
  if (!ua && cachedResult) {
    return cachedResult;
  }

  const result = parseAppEnv(getUA(ua));

  // 客户端缓存逻辑：无参数时缓存结果
  if (!ua) {
    cachedResult = result;
  }

  return result;
}

/**
 * 获取美团微信小程序版本号
 * @param url - 可选的 URL 字符串
 * @returns 版本号字符串，没有返回 null
 */
export function getMtWXAppVersion(url?: string): string | null {
  return getUrlParam('app_version', url);
}

/**
 * 获取跳转环境对应的灵犀埋点参数
 * @param jumpType 
 * @param lxEnv 
 * @returns 
 */
export function getLxJumpTargetEnv(jumpType: JumpTypeEnum, lxEnv: LX_ENV_TYPE) {
  switch (jumpType) {
    case JumpTypeEnum.MT_WXAPP:
      return LX_ENV_TYPE.MT_MINI;
    case JumpTypeEnum.MT_APP:
      return LX_ENV_TYPE.MT_APP;
    case JumpTypeEnum.H5:
    default:
      return lxEnv;
  }
}
